import { useState, useEffect } from "react";
import { X, Globe, Zap, Bot } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { publishCapsule, publishAgentFromChat } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = ["general", "research", "coding", "finance", "support"];

export default function PublishModal({ open, onClose, chat, user }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [previewPrice, setPreviewPrice] = useState("");
  const [passPrice, setPassPrice] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && chat) {
      setTitle(chat.title || "");
      setDescription("");
      setPreviewPrice("");
      setPassPrice("");
      setDurationDays("");
    }
  }, [open, chat]);

  const hasPricing = previewPrice !== "" && passPrice !== "" && durationDays !== "";
  const isAgentMode = !hasPricing;

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to publish"); return; }
    if (!chat) return;
    if (!title.trim()) { toast.error("Title is required"); return; }
    setLoading(true);

    try {
      if (isAgentMode) {
        await publishAgentFromChat({
          chat_id: chat.chat_id || chat.id,
          name: title.trim(),
          description: description.trim(),
          tags: category !== "general" ? [category] : [],
        });
        toast.success("Published to Agent Network");
      } else {
        const pp = parseFloat(previewPrice);
        const paP = parseFloat(passPrice);
        const dur = parseInt(durationDays);
        if (isNaN(pp) || pp < 0.50) { toast.error("Preview price must be at least $0.50"); setLoading(false); return; }
        if (isNaN(paP) || paP < 0.01) { toast.error("Pass price is required"); setLoading(false); return; }
        if (isNaN(dur) || dur < 1) { toast.error("Duration must be at least 1 day"); setLoading(false); return; }
        await publishCapsule({
          chat_id: chat.chat_id || chat.id,
          title: title.trim(),
          description: description.trim(),
          category,
          preview_price: pp,
          pass_price: paP,
          duration_days: dur,
        });
        toast.success("Published to Marketplace");
      }
      onClose(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="publish-modal"
        className="bg-[#111620] border border-[#273142] shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-0 rounded-2xl max-w-md [&>button]:hidden"
      >
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe size={18} className="text-[#E5FF00]" />
              <DialogTitle className="text-lg font-semibold tracking-tight">PUBLISH</DialogTitle>
            </div>
            <button data-testid="close-publish-modal" onClick={() => onClose(false)} className="text-[#475569] hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
        </DialogHeader>

        <form onSubmit={handlePublish} className="p-6 pt-4 space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">Title</Label>
            <Input data-testid="publish-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Agent name..." className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] text-sm" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">Description</Label>
            <Textarea data-testid="publish-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="What does this agent do?" className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] text-sm resize-none" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="publish-category" className="bg-[#080B10] border-[#273142] text-white rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111620] border-[#273142] rounded-none">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="text-white hover:bg-[#1C2433] rounded-none font-mono text-xs uppercase">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pricing — optional. Empty = free agent, filled = marketplace */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8]">Pricing</Label>
              <span className="text-[10px] font-mono text-[#475569]">leave empty for free agent</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] font-mono uppercase text-[#475569] mb-1 block">Preview $/query</Label>
                <Input data-testid="publish-preview-price" type="number" step="0.01" min="0.50" value={previewPrice} onChange={(e) => setPreviewPrice(e.target.value)}
                  placeholder="--"
                  className="bg-[#080B10] border-[#273142] text-white rounded-lg font-mono text-sm placeholder:text-[#273142]" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase text-[#475569] mb-1 block">Pass $</Label>
                <Input data-testid="publish-pass-price" type="number" step="0.01" min="0.01" value={passPrice} onChange={(e) => setPassPrice(e.target.value)}
                  placeholder="--"
                  className="bg-[#080B10] border-[#273142] text-white rounded-lg font-mono text-sm placeholder:text-[#273142]" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase text-[#475569] mb-1 block">Days</Label>
                <Input data-testid="publish-duration" type="number" min="1" value={durationDays} onChange={(e) => setDurationDays(e.target.value)}
                  placeholder="--"
                  className="bg-[#080B10] border-[#273142] text-white rounded-lg font-mono text-sm placeholder:text-[#273142]" />
              </div>
            </div>
          </div>

          {/* Destination indicator */}
          <div data-testid="publish-destination-indicator" className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
            isAgentMode
              ? "bg-[#10B981]/5 border-[#10B981]/20"
              : "bg-[#E5FF00]/5 border-[#E5FF00]/20"
          }`}>
            {isAgentMode ? (
              <>
                <Bot size={14} className="text-[#10B981]" />
                <span className="font-mono text-[10px] text-[#10B981] uppercase tracking-wider">Publishes as free agent to Agent Network</span>
              </>
            ) : (
              <>
                <Zap size={14} className="text-[#E5FF00]" />
                <span className="font-mono text-[10px] text-[#E5FF00] uppercase tracking-wider">Publishes as paid capsule to Marketplace</span>
              </>
            )}
          </div>

          <button type="submit" data-testid="publish-submit" disabled={loading || !title.trim()}
            className={`w-full font-semibold uppercase tracking-wider text-xs px-4 py-2.5 transition-colors rounded-lg disabled:opacity-50 ${
              isAgentMode
                ? "bg-[#10B981] text-[#080B10] hover:bg-[#0D9668]"
                : "bg-[#E5FF00] text-[#080B10] hover:bg-[#D4EC00]"
            }`}>
            {loading ? "PUBLISHING..." : isAgentMode ? "PUBLISH TO AGENT NETWORK" : "PUBLISH TO MARKETPLACE"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
